/**
 * MetricCard.jsx — One reusable metric tile with consistent typography & spacing.
 *
 * Visual hierarchy:
 *   - Uppercase micro-label (text-[10px], tracking-wider, slate-400)
 *   - Big, tabular-num value (text-2xl/3xl, semibold)
 *   - Optional sublabel (text-[10px], slate-400)
 *   - Optional tone color for the value (positive/neutral/negative/info)
 *
 * Used inside <MetricStrip/> and standalone for status cards.
 */
import { cn } from '../../lib/utils';

const TONE = {
  positive: 'text-positive-soft-foreground ',
  neutral: 'text-warning-soft-foreground ',
  negative: 'text-negative-soft-foreground ',
  info: 'text-blue-700 dark:text-blue-400',
  default: 'text-slate-900 dark:text-slate-100',
};

const SIZE = {
  sm: 'text-lg',
  md: 'text-2xl',
  lg: 'text-3xl',
};

export default function MetricCard({
  label,
  value,
  sublabel = null,
  tone = 'default',
  size = 'md',
  icon: Icon = null,
  className,
  testId,
}) {
  const valueCls = TONE[tone] || TONE.default;
  const sizeCls = SIZE[size] || SIZE.md;

  return (
    <div
      className={cn(
        'bg-white dark:bg-card p-4 flex flex-col justify-between min-h-[88px]',
        className
      )}
      data-testid={testId}
    >
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <p className="text-[10px] uppercase tracking-wider text-slate-400 dark:text-slate-500 font-semibold truncate">
          {label}
        </p>
        {Icon && <Icon className="w-3.5 h-3.5 text-slate-300 dark:text-slate-600 flex-shrink-0" />}
      </div>
      <p className={cn('font-semibold tabular-nums leading-none', sizeCls, valueCls)} title={typeof value === 'string' ? value : undefined}>
        {value}
      </p>
      {sublabel && (
        <p className="text-[10px] text-slate-400 dark:text-slate-500 mt-1.5 truncate" title={typeof sublabel === 'string' ? sublabel : undefined}>
          {sublabel}
        </p>
      )}
    </div>
  );
}
